import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Plus, Search, CreditCard, Calendar, Edit, Trash2, 
  RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AccountingHesapKartlari = () => {
  const [kartlar, setKartlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedKart, setSelectedKart] = useState(null);
  const [formData, setFormData] = useState({
    kayit_adi: ''
  });

  useEffect(() => {
    loadKartlar();
  }, []);

  const loadKartlar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounting/hesap-kartlari`, { headers: getAuthHeaders() });
      setKartlar(res.data || []);
    } catch (error) {
      console.error('Hesap kartları yüklenirken hata:', error);
      toast.error('Hesap kartları yüklenemedi');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formData.kayit_adi.trim()) {
      toast.error('Kayıt adı gerekli');
      return;
    }
    try {
      const res = await axios.post(`${API}/accounting/hesap-kartlari`, formData, { headers: getAuthHeaders() });
      if (res.status === 200 || res.status === 201) {
        toast.success('Hesap kartı eklendi');
        setModalOpen(false);
        setFormData({ kayit_adi: '' });
        loadKartlar();
      } else {
        toast.error('Hesap kartı eklenemedi');
      }
    } catch (error) {
      console.error('Hesap kartı ekleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Hesap kartı eklenemedi');
    }
  };

  const handleUpdate = async () => {
    if (!formData.kayit_adi.trim()) {
      toast.error('Kayıt adı gerekli');
      return;
    }
    try {
      const res = await axios.put(`${API}/accounting/hesap-kartlari/${selectedKart.id}`, formData, { headers: getAuthHeaders() });
      if (res.status === 200 || res.status === 201) {
        toast.success('Hesap kartı güncellendi');
        setEditModalOpen(false);
        setSelectedKart(null);
        setFormData({ kayit_adi: '' });
        loadKartlar();
      } else {
        toast.error('Hesap kartı güncellenemedi');
      }
    } catch (error) {
      console.error('Hesap kartı güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Hesap kartı güncellenemedi');
    }
  };

  const handleDelete = async (kart) => {
    if (!window.confirm(`"${kart.kayit_adi}" hesap kartını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/accounting/hesap-kartlari/${kart.id}`, { headers: getAuthHeaders() });
      toast.success('Hesap kartı silindi');
      loadKartlar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hesap kartı silinemedi');
    }
  };

  const openEditModal = (kart) => {
    setSelectedKart(kart);
    setFormData({
      kayit_adi: kart.kayit_adi || ''
    });
    setEditModalOpen(true);
  };

  const filteredKartlar = kartlar.filter(k => 
    k.kayit_adi?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="hesap-kartlari-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HESAP KARTLARI</h1>
          <p className="text-gray-500">Hesap kartı kayıtlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadKartlar} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button 
            onClick={() => { setFormData({ kayit_adi: '' }); setModalOpen(true); }}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-hesap-karti-btn"
          >
            <Plus className="h-4 w-4" />
            Hesap Kartı Ekle
          </Button>
        </div>
      </div>

      {/* İstatistik Kartı */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-3">
          <CreditCard className="h-8 w-8 text-purple-600" />
          <div>
            <p className="text-2xl font-bold text-purple-700">{kartlar.length}</p>
            <p className="text-sm text-purple-600">Toplam Hesap Kartı</p>
          </div>
        </CardContent>
      </Card>

      {/* Arama */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Hesap kartı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-hesap-karti"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tablo */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : filteredKartlar.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CreditCard className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Hesap Kartı Bulunmuyor</h3>
            <p className="text-gray-500">Henüz hesap kartı eklenmemiş</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Oluşturma Tarihi</TableHead>
                  <TableHead>Kayıt Adı</TableHead>
                  <TableHead>Oluşturan</TableHead>
                  <TableHead>Güncellenme</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredKartlar.map(kart => (
                  <TableRow key={kart.id} data-testid={`kart-row-${kart.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        {new Date(kart.created_at).toLocaleDateString('tr-TR')} {new Date(kart.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{kart.kayit_adi}</TableCell>
                    <TableCell className="text-sm text-gray-500">{kart.created_by_name || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {kart.updated_at ? new Date(kart.updated_at).toLocaleDateString('tr-TR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditModal(kart)}
                          data-testid={`edit-kart-${kart.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(kart)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-kart-${kart.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Ekleme Modalı */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-purple-600" />
              Yeni Hesap Kartı Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <Label className="text-gray-500 text-sm">Oluşturma Tarihi</Label>
              <p className="font-medium">{new Date().toLocaleDateString('tr-TR')} {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            
            <div>
              <Label htmlFor="kayit_adi">Kayıt Adı *</Label>
              <Input 
                id="kayit_adi"
                value={formData.kayit_adi}
                onChange={(e) => setFormData({ ...formData, kayit_adi: e.target.value })}
                placeholder="Kayıt adını girin"
                data-testid="kayit-adi-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-kart-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-purple-600" />
              Hesap Kartı Düzenle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <Label className="text-gray-500 text-sm">Oluşturma Tarihi</Label>
              <p className="font-medium">
                {selectedKart ? `${new Date(selectedKart.created_at).toLocaleDateString('tr-TR')} ${new Date(selectedKart.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : '-'}
              </p>
            </div>
            
            <div>
              <Label htmlFor="edit-kayit_adi">Kayıt Adı *</Label>
              <Input 
                id="edit-kayit_adi"
                value={formData.kayit_adi}
                onChange={(e) => setFormData({ ...formData, kayit_adi: e.target.value })}
                placeholder="Kayıt adını girin"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdate}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingHesapKartlari;
